import { formatPlainTime, isPlainTime } from '@/lib/time';

/** Parses typed wall-clock text ("2:30 pm", "1430", "14:30", "9a") into "HH:mm". */
export function parseTimeInput(text: string): string | null {
  const normalized = text.trim().toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ');
  const match = /^(\d{1,2})(?::?(\d{2}))?\s?(a|am|p|pm)?$/.exec(normalized);
  if (!match) return null;
  let hours = Number(match[1]);
  const minutes = match[2] ? Number(match[2]) : 0;
  const meridiem = match[3];
  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    if (meridiem.startsWith('p') && hours !== 12) hours += 12;
    if (meridiem.startsWith('a') && hours === 12) hours = 0;
  }
  if (hours > 23 || minutes > 59) return null;
  const value = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  return isPlainTime(value) ? value : null;
}

export function minutesOf(time: string): number {
  const [h, m] = time.split(':');
  return Number(h) * 60 + Number(m);
}

export interface TimeOption {
  value: string;
  label: string;
}

/** Suggested times every `step` minutes within [min, max] ("HH:mm"). */
export function buildTimeOptions(
  step: number,
  locale: string | undefined,
  min?: string,
  max?: string,
): TimeOption[] {
  const safeStep = Math.max(1, Math.min(720, Math.floor(step)));
  const start = min && isPlainTime(min) ? minutesOf(min) : 0;
  const end = max && isPlainTime(max) ? minutesOf(max) : 24 * 60 - 1;
  const options: TimeOption[] = [];
  for (let total = 0; total < 24 * 60; total += safeStep) {
    if (total < start || total > end) continue;
    const value = `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
    options.push({ value, label: formatPlainTime(value, locale) });
  }
  return options;
}

/** Options matching partially typed text (by label or HH:mm). Falls back to all options. */
export function filterTimeOptions(options: TimeOption[], text: string): TimeOption[] {
  const q = text.trim().toLowerCase().replace(/\s+/g, '');
  if (!q) return options;
  const matches = options.filter((o) => {
    const label = o.label.toLowerCase().replace(/\s+/g, '');
    return label.startsWith(q) || o.value.startsWith(q) || o.value.replace(':', '').startsWith(q);
  });
  return matches.length > 0 ? matches : options;
}
